// frontend/src/app/admin/OverviewPage.tsx
import React, { useEffect, useMemo, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import StatCard from "./observability/components/StatCard";
import TimeSeriesChart from "./observability/components/TimeSeriesChart";
import TopKTable from "./observability/components/TopKTable";
import { promQuery, promQueryRange, rangeWindowMs, toSeries } from "./observability/prom";
import { getTenantUuid } from "@/app/ingress/tenant";
import FirstTimeWelcomeModal from "@/app/components/FirstTimeWelcomeModal";

type Row = Record<string, any>;

function fmt(n: any, digits = 2) {
  const x = typeof n === "number" ? n : parseFloat(String(n));
  if (!Number.isFinite(x)) return "—";
  return x.toFixed(digits);
}

function fmtInt(n: any) {
  const x = typeof n === "number" ? n : parseFloat(String(n));
  if (!Number.isFinite(x)) return "—";
  return String(Math.round(x));
}

function removeQueryParams(search: string, keys: string[]): string {
  const sp = new URLSearchParams(search || "");
  for (const k of keys) sp.delete(k);
  const next = sp.toString();
  return next ? `?${next}` : "";
}

function readAuthSnapshot(): { tenant?: string; roles?: string[] } {
  try {
    const raw = localStorage.getItem("orchestrator.authme");
    if (!raw) return {};
    const parsed = JSON.parse(raw);
    const tenant = parsed?.me?.tenant;
    const roles = parsed?.me?.roles;
    return {
      tenant: typeof tenant === "string" ? tenant : undefined,
      roles: Array.isArray(roles) ? roles.map(String) : undefined,
    };
  } catch {
    return {};
  }
}

class OverviewErrorBoundary extends React.Component<
  { children: React.ReactNode },
  { hasError: boolean; message?: string }
> {
  constructor(props: any) {
    super(props);
    this.state = { hasError: false, message: undefined };
  }

  static getDerivedStateFromError(err: any) {
    return { hasError: true, message: String(err?.message || err) };
  }

  componentDidCatch(err: any) {
    // eslint-disable-next-line no-console
    console.error("[OverviewErrorBoundary]", err);
  }

  render() {
    if (this.state.hasError) {
      return (
        <div className="p-6">
          <div className="text-xl font-semibold text-red-700">Overview crashed</div>
          <div className="mt-2 text-sm text-zinc-700">
            Open DevTools → Console for the full stack trace.
          </div>
          <pre className="mt-3 text-xs bg-zinc-100 p-3 rounded overflow-auto">
            {this.state.message}
          </pre>
        </div>
      );
    }
    return this.props.children as any;
  }
}

export default function OverviewPage() {
  const tenantUuid = getTenantUuid();

  // --- First-time welcome modal (trigger-only via ?first_time=true) ---
  const location = useLocation();
  const navigate = useNavigate();
  const [showWelcome, setShowWelcome] = useState(false);

  const authSnap = useMemo(() => readAuthSnapshot(), []);
  const roles = authSnap.roles || [];

  const firstTime = useMemo(() => {
    const sp = new URLSearchParams(location.search || "");
    return sp.get("first_time") === "true";
  }, [location.search]);

  useEffect(() => {
    if (!firstTime) return;

    setShowWelcome(true);

    // Clear param immediately (trigger only)
    const cleaned = removeQueryParams(location.search, ["first_time"]);
    navigate({ pathname: location.pathname, search: cleaned }, { replace: true });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [firstTime]);

  // Stats
  const [tokensPromptPerMin, setTokensPromptPerMin] = useState<number | null>(null);
  const [tokensCompletionPerMin, setTokensCompletionPerMin] = useState<number | null>(null);
  const [totalTokensPerMin, setTotalTokensPerMin] = useState<number | null>(null);
  const [costUsdPerMin, setCostUsdPerMin] = useState<number | null>(null);
  const [callsPerMin, setCallsPerMin] = useState<number | null>(null);

  // Tables
  const [topTokenModels, setTopTokenModels] = useState<Row[]>([]);
  const [topCostModels, setTopCostModels] = useState<Row[]>([]);
  const [topCallsModels, setTopCallsModels] = useState<Row[]>([]);

  // Time series charts
  const [tokensSeries, setTokensSeries] = useState<any[]>([]);
  const [costSeries, setCostSeries] = useState<any[]>([]);
  const [costPerReqSeries, setCostPerReqSeries] = useState<any[]>([]);

  useEffect(() => {
    let alive = true;

    async function run() {
      const { start, end } = rangeWindowMs(2);

      // 1) HEADLINE STATS
      try {
        const q = `sum(rate(llm_tokens_prompt_total_by_tenant_total{tenant_id="${tenantUuid}"}[5m])) * 60`;
        const resp = await promQuery(q, tenantUuid);
        const ss = toSeries(resp);
        const v = ss[0]?.points?.[0]?.v ?? null;
        if (!alive) return;
        setTokensPromptPerMin(typeof v === "number" ? v : null);
      } catch {
        if (!alive) return;
        setTokensPromptPerMin(null);
      }

      try {
        const q = `sum(rate(llm_tokens_completion_total_by_tenant_total{tenant_id="${tenantUuid}"}[5m])) * 60`;
        const resp = await promQuery(q, tenantUuid);
        const ss = toSeries(resp);
        const v = ss[0]?.points?.[0]?.v ?? null;
        if (!alive) return;
        setTokensCompletionPerMin(typeof v === "number" ? v : null);
      } catch {
        if (!alive) return;
        setTokensCompletionPerMin(null);
      }

      try {
        const q = `(sum(rate(llm_tokens_prompt_total_by_tenant_total{tenant_id="${tenantUuid}"}[5m])) + sum(rate(llm_tokens_completion_total_by_tenant_total{tenant_id="${tenantUuid}"}[5m]))) * 60`;
        const resp = await promQuery(q, tenantUuid);
        const ss = toSeries(resp);
        const v = ss[0]?.points?.[0]?.v ?? null;
        if (!alive) return;
        setTotalTokensPerMin(typeof v === "number" ? v : null);
      } catch {
        if (!alive) return;
        setTotalTokensPerMin(null);
      }

      try {
        const q = `sum(rate(llm_cost_usd_total_by_tenant_total{tenant_id="${tenantUuid}"}[5m])) * 60`;
        const resp = await promQuery(q, tenantUuid);
        const ss = toSeries(resp);
        const v = ss[0]?.points?.[0]?.v ?? null;
        if (!alive) return;
        setCostUsdPerMin(typeof v === "number" ? v : null);
      } catch {
        if (!alive) return;
        setCostUsdPerMin(null);
      }

      try {
        const q = `sum(rate(llm_calls_total_by_tenant_total{tenant_id="${tenantUuid}",model!="unknown"}[5m])) * 60`;
        const resp = await promQuery(q, tenantUuid);
        const ss = toSeries(resp);
        const v = ss[0]?.points?.[0]?.v ?? null;
        if (!alive) return;
        setCallsPerMin(typeof v === "number" ? v : null);
      } catch {
        if (!alive) return;
        setCallsPerMin(null);
      }

      // 2) TOP TABLES
      try {
        const q = `topk(10, sum by (provider,model) (increase(llm_tokens_prompt_total_by_tenant_total{tenant_id="${tenantUuid}"}[15m])))`;
        const resp = await promQuery(q, tenantUuid);
        const ss = toSeries(resp);
        const rows = ss.map((s) => ({
          provider: s.metric.provider || "unknown",
          model: s.metric.model || "unknown",
          prompt_tokens_15m: s.points[0]?.v ?? 0,
        }));
        if (!alive) return;
        setTopTokenModels(rows.sort((a, b) => (b.prompt_tokens_15m ?? 0) - (a.prompt_tokens_15m ?? 0)));
      } catch {
        if (!alive) return;
        setTopTokenModels([]);
      }

      try {
        const q = `topk(10, sum by (provider,model) (increase(llm_cost_usd_total_by_tenant_total{tenant_id="${tenantUuid}"}[15m])))`;
        const resp = await promQuery(q, tenantUuid);
        const ss = toSeries(resp);
        const rows = ss.map((s) => ({
          provider: s.metric.provider || "unknown",
          model: s.metric.model || "unknown",
          cost_usd_15m: s.points[0]?.v ?? 0,
        }));
        if (!alive) return;
        setTopCostModels(rows.sort((a, b) => (b.cost_usd_15m ?? 0) - (a.cost_usd_15m ?? 0)));
      } catch {
        if (!alive) return;
        setTopCostModels([]);
      }

      try {
        const q = `topk(10, sum by (provider,model) (increase(llm_calls_total_by_tenant_total{tenant_id="${tenantUuid}",model!="unknown"}[15m])))`;
        const resp = await promQuery(q, tenantUuid);
        const ss = toSeries(resp);
        const rows = ss.map((s) => ({
          provider: s.metric.provider || "unknown",
          model: s.metric.model || "unknown",
          calls_15m: s.points[0]?.v ?? 0,
        }));
        if (!alive) return;
        setTopCallsModels(rows.sort((a, b) => (b.calls_15m ?? 0) - (a.calls_15m ?? 0)));
      } catch {
        if (!alive) return;
        setTopCallsModels([]);
      }

      // 3) TIME SERIES
      try {
        const qPrompt = `sum(rate(llm_tokens_prompt_total_by_tenant_total{tenant_id="${tenantUuid}"}[5m])) * 60`;
        const qComp = `sum(rate(llm_tokens_completion_total_by_tenant_total{tenant_id="${tenantUuid}"}[5m])) * 60`;

        const rp = await promQueryRange(qPrompt, tenantUuid, { start, end, step: "30s" });
        const rc = await promQueryRange(qComp, tenantUuid, { start, end, step: "30s" });

        const sp = toSeries(rp)[0]?.points || [];
        const sc = toSeries(rc)[0]?.points || [];

        if (!alive) return;
        setTokensSeries([
          { name: "prompt_tokens/min", points: sp },
          { name: "completion_tokens/min", points: sc },
        ]);
      } catch {
        if (!alive) return;
        setTokensSeries([]);
      }

      try {
        const q = `sum(rate(llm_cost_usd_total_by_tenant_total{tenant_id="${tenantUuid}"}[5m])) * 60`;
        const resp = await promQueryRange(q, tenantUuid, { start, end, step: "30s" });
        const ss = toSeries(resp);
        const pts = ss[0]?.points || [];
        if (!alive) return;
        setCostSeries([{ name: "usd/min", points: pts }]);
      } catch {
        if (!alive) return;
        setCostSeries([]);
      }

      try {
        const q50 = `histogram_quantile(0.50, sum by (le,slug) (rate(request_cost_usd_by_tenant_bucket{tenant_id="${tenantUuid}"}[15m])))`;
        const q95 = `histogram_quantile(0.95, sum by (le,slug) (rate(request_cost_usd_by_tenant_bucket{tenant_id="${tenantUuid}"}[15m])))`;

        const r50 = await promQueryRange(q50, tenantUuid, { start, end, step: "60s" });
        const r95 = await promQueryRange(q95, tenantUuid, { start, end, step: "60s" });

        const s50 = toSeries(r50).map((s) => ({ name: `p50 ${s.metric.slug || "unknown"}`, points: s.points }));
        const s95 = toSeries(r95).map((s) => ({ name: `p95 ${s.metric.slug || "unknown"}`, points: s.points }));

        const latest = (x: any) => (x?.points?.length ? x.points[x.points.length - 1].v : 0);
        const topByP95 = [...s95].sort((a, b) => latest(b) - latest(a)).slice(0, 6);

        const topSlugs = new Set(topByP95.map((s) => String(s.name).replace(/^p95\s+/, "")));
        const topP50 = s50.filter((s) => topSlugs.has(String(s.name).replace(/^p50\s+/, "")));

        if (!alive) return;
        setCostPerReqSeries([...topByP95, ...topP50]);
      } catch {
        if (!alive) return;
        setCostPerReqSeries([]);
      }
    }

    run();
    const t = window.setInterval(run, 20_000);
    return () => {
      alive = false;
      window.clearInterval(t);
    };
  }, [tenantUuid]);

  const topToken = topTokenModels[0];
  const topCost = topCostModels[0];
  const topCalls = topCallsModels[0];

  const costBurnLabel = useMemo(() => {
    if (costUsdPerMin == null) return "—";
    if (!Number.isFinite(costUsdPerMin)) return "—";
    return `$${fmt(costUsdPerMin, 4)}/min`;
  }, [costUsdPerMin]);

  return (
    <OverviewErrorBoundary>
      <div className="p-6 space-y-6">
        <FirstTimeWelcomeModal
          open={showWelcome}
          onClose={() => setShowWelcome(false)}
          tenantUuid={tenantUuid}
          roles={roles}
        />

        <div className="flex items-baseline justify-between gap-4">
          <div>
            <div className="text-xl font-semibold">Admin · Overview</div>
            <div className="text-xs text-gray-500 mt-1">
              Prometheus via <code>/v1/admin/observability/prom/query(_range)</code> · tenant{" "}
              <code>{tenantUuid.slice(0, 8)}…</code>
            </div>
          </div>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-5 gap-4">
          <StatCard title="Tokens/min (prompt)" value={tokensPromptPerMin == null ? "—" : fmtInt(tokensPromptPerMin)} />
          <StatCard
            title="Tokens/min (completion)"
            value={tokensCompletionPerMin == null ? "—" : fmtInt(tokensCompletionPerMin)}
          />
          <StatCard title="Tokens/min (total)" value={totalTokensPerMin == null ? "—" : fmtInt(totalTokensPerMin)} />
          <StatCard title="Calls/min" value={callsPerMin == null ? "—" : fmt(callsPerMin, 2)} />
          <StatCard
            title="Cost burn"
            value={costBurnLabel}
            sub={topCost ? `top spender: ${topCost.provider} · ${topCost.model}` : undefined}
          />
        </div>

        <div className="grid grid-cols-1 xl:grid-cols-2 gap-6">
          <TimeSeriesChart title="LLM token throughput (per minute, rolling 5m)" series={tokensSeries} height={320} />
          <TimeSeriesChart title="LLM cost burn (USD/min, rolling 5m)" series={costSeries} height={320} />
        </div>

        <div className="grid grid-cols-1 xl:grid-cols-3 gap-6">
          <TopKTable
            title="Top token usage (prompt tokens, last 15m)"
            columns={[
              { key: "provider", label: "provider", mono: true },
              { key: "model", label: "model", mono: true },
              { key: "prompt_tokens_15m", label: "prompt_tokens_15m" },
            ]}
            rows={topTokenModels.map((r) => ({ ...r, prompt_tokens_15m: fmtInt(r.prompt_tokens_15m) }))}
          />

          <TopKTable
            title="Top LLM calls (last 15m)"
            columns={[
              { key: "provider", label: "provider", mono: true },
              { key: "model", label: "model", mono: true },
              { key: "calls_15m", label: "calls_15m" },
            ]}
            rows={topCallsModels.map((r) => ({ ...r, calls_15m: fmtInt(r.calls_15m) }))}
          />

          <TopKTable
            title="Top cost (USD, last 15m)"
            columns={[
              { key: "provider", label: "provider", mono: true },
              { key: "model", label: "model", mono: true },
              { key: "cost_usd_15m", label: "cost_usd_15m" },
            ]}
            rows={topCostModels.map((r) => ({ ...r, cost_usd_15m: `$${fmt(r.cost_usd_15m, 6)}` }))}
          />
        </div>

        <div className="grid grid-cols-1 gap-6">
          <TimeSeriesChart title="Cost per request (USD) p50/p95 by slug (rolling 15m)" series={costPerReqSeries} height={360} />
        </div>

        <div className="text-xs text-gray-500">
          <div>
            Top model by tokens: <b>{topToken ? `${topToken.provider} · ${topToken.model}` : "—"}</b>
          </div>
          <div>
            Top model by calls: <b>{topCalls ? `${topCalls.provider} · ${topCalls.model}` : "—"}</b>
          </div>
        </div>
      </div>
    </OverviewErrorBoundary>
  );
}